import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import { X, Search } from 'lucide-react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Avatar } from '../ui/avatar'
import LoadingSpinner from '../leevo_ui/LoadingSpinner/LoadingSpinner'

function NewConversationModal({ isOpen, onClose, onCreateConversation }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [guardians, setGuardians] = useState([])
  const [directors, setDirectors] = useState([])
  const [staff, setStaff] = useState([])
  const [facilities, setFacilities] = useState([])
  const [superusers, setSuperusers] = useState([])
  const [viewType, setViewType] = useState('default')
  const [isLoading, setIsLoading] = useState(false)
  const [isCreating, setIsCreating] = useState(false)

  useEffect(() => {
    if (isOpen) {
      fetchUsers()
    }
  }, [isOpen])

  const fetchUsers = async () => {
    setIsLoading(true)
    try {
      const response = await axios.get('/api/messageable_users')
      const data = response.data.data

      if (data?.facilities) {
        setViewType('superuser')
        setFacilities(data.facilities || [])
        setSuperusers([])
        setDirectors([])
        setStaff([])
        setGuardians([])
      } else if (data?.superusers !== undefined) {
        setViewType('staff')
        setSuperusers(data.superusers || [])
        setDirectors(data.directors || [])
        setStaff(data.staff || [])
        setGuardians(data.guardians || [])
        setFacilities([])
      } else {
        setViewType('guardian')
        setDirectors(data?.directors || [])
        setStaff(data?.staff || [])
        setSuperusers([])
        setGuardians([])
        setFacilities([])
      }
    } catch (error) {
      console.error('Error fetching users:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateConversation = async (userId) => {
    setIsCreating(true)
    try {
      await onCreateConversation(userId)
      onClose()
    } catch (error) {
      console.error('Error creating conversation:', error)
    } finally {
      setIsCreating(false)
    }
  }

  const filterUsers = (userList) => {
    if (!searchTerm) return userList
    const term = searchTerm.toLowerCase()
    return userList.filter((user) => {
      const preferredName = (user.attributes?.preferredName || '').toLowerCase()
      const fullName = (user.attributes?.fullName || '').toLowerCase()
      return preferredName.includes(term) || fullName.includes(term)
    })
  }

  const filteredGuardians = filterUsers(guardians)
  const filteredDirectors = filterUsers(directors)
  const filteredStaff = filterUsers(staff)
  const filteredSuperusers = filterUsers(superusers)
  const filteredFacilities = facilities
    .map((f) => ({
      ...f,
      directors: filterUsers(f.directors || []),
      staff: filterUsers(f.staff || [])
    }))
    .filter((f) => f.directors.length > 0 || f.staff.length > 0)

  const hasResults =
    viewType === 'superuser'
      ? filteredFacilities.length > 0
      : viewType === 'staff'
        ? filteredSuperusers.length > 0 ||
          filteredDirectors.length > 0 ||
          filteredStaff.length > 0 ||
          filteredGuardians.length > 0
        : filteredDirectors.length > 0 || filteredStaff.length > 0

  if (!isOpen) return null

  const renderUserList = (userList, title) => {
    if (userList.length === 0) return null

    return (
      <div key={title}>
        <h3 className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase bg-gray-50 sticky top-0 z-10">
          {title}
        </h3>
        <div className="divide-y divide-gray-200">
          {userList.map((user) => {
            const attrs = user.attributes || {}
            return (
              <button
                key={user.id}
                onClick={() => handleCreateConversation(user.id)}
                disabled={isCreating}
                className="w-full p-4 text-left transition-colors hover:bg-gray-50 disabled:opacity-50"
              >
                <div className="flex items-center gap-3">
                  <Avatar className="w-10 h-10">
                    {attrs.cloudinaryPhotoPublicId ? (
                      <img
                        src={`https://res.cloudinary.com/${process.env.CLOUDINARY_CLOUD_NAME}/image/upload/c_fill,g_face,h_40,w_40/${attrs.cloudinaryPhotoPublicId}.jpg`}
                        alt={attrs.preferredName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-purple-200 flex items-center justify-center text-purple-700 font-semibold">
                        {(attrs.preferredName || '?')[0].toUpperCase()}
                      </div>
                    )}
                  </Avatar>
                  <div>
                    <p className="font-medium text-gray-900">{attrs.preferredName}</p>
                    {attrs.fullName && attrs.fullName !== attrs.preferredName && (
                      <p className="text-sm text-gray-500">{attrs.fullName}</p>
                    )}
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">New Conversation</h2>
          <Button onClick={onClose} variant="ghost" size="icon">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <LoadingSpinner size="8" color="purple" />
            </div>
          ) : !hasResults ? (
            <div className="flex items-center justify-center p-8 text-center">
              <p className="text-sm text-gray-500">No users found</p>
            </div>
          ) : viewType === 'superuser' ? (
            <>
              {filteredFacilities.map((facility) => (
                <div key={facility.facilityId}>
                  <h3 className="px-4 py-2 text-xs font-semibold text-purple-700 uppercase bg-purple-50 sticky top-0 z-10">
                    {facility.facilityName}
                  </h3>
                  {renderUserList(facility.directors, 'Directors')}
                  {renderUserList(facility.staff, 'Staff')}
                </div>
              ))}
            </>
          ) : viewType === 'staff' ? (
            <>
              {renderUserList(filteredSuperusers, 'Superusers')}
              {renderUserList(filteredDirectors, 'Directors')}
              {renderUserList(filteredStaff, 'Staff')}
              {renderUserList(filteredGuardians, 'Guardians')}
            </>
          ) : (
            <>
              {renderUserList(filteredDirectors, 'Directors')}
              {renderUserList(filteredStaff, 'Staff')}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

NewConversationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreateConversation: PropTypes.func.isRequired
}

export default NewConversationModal
